import React, {
  FC,
  HTMLAttributes,
  MouseEvent,
  ReactElement,
  RefObject,
  SVGProps,
  useRef,
  useState,
} from 'react';

import { IconButton, IconItem, ListIcon, Menu } from '@scaleaq/scaleui';

import { Z_INDEX } from '@/constants/zIndex';
import { Icon } from '@/utils/scaleUi';

type MenuPlacement = 'top' | 'bottom' | 'left' | 'right' | 'bottom-start' | 'bottom-end';

type ToolbarProps = Omit<HTMLAttributes<HTMLButtonElement>, 'color'> & {
  actionList: IconItem[];
  closeOnOutsideClick?: boolean;
  disabled?: boolean;
  Icon?: React.FC<SVGProps<SVGSVGElement>> | ReactElement;
  menuPlacement?: MenuPlacement;
  zIndex?: number;
  usePortal?: boolean;
};
export const Toolbar = ({
  actionList,
  closeOnOutsideClick = true,
  disabled,
  Icon: ToggleIcon = <Icon.EllipsisVertical size='large' />,
  menuPlacement = 'bottom',
  style,
  zIndex = Z_INDEX.base.elevated,
  usePortal = false,
  onClick: onButtonClick,
  onMouseDown: onButtonMouseDown,
  ...rest
}: ToolbarProps) => {
  const [openMenu, setOpenMenu] = useState(false);
  const ref = useRef<HTMLElement>(null);

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenMenu(true);
    onButtonClick?.(e);
  };

  const handleButtonMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onButtonMouseDown?.(e);
  };

  return (
    <Menu
      usePortal={usePortal}
      placement={menuPlacement}
      zIndex={zIndex}
      anchorEl={ref as RefObject<HTMLElement>}
      open={openMenu}
      onClose={() => setOpenMenu(false)}
      onMouseDown={e => e.stopPropagation()}
      closeOnOutsideClick={closeOnOutsideClick}
      menuElement={
        <ListIcon
          style={{ maxHeight: '24rem' }}
          items={actionList.map(li => ({
            key: li.key,
            ...li,
            onClick: (
              val: { Icon?: FC<SVGProps<SVGSVGElement>> },
              ev?: MouseEvent<HTMLElement>
            ) => {
              setOpenMenu(false);
              li.onClick?.(val, ev);
            },
          }))}
        />
      }
    >
      <IconButton
        disabled={disabled}
        ref={ref as RefObject<HTMLButtonElement>}
        size='large'
        Icon={ToggleIcon}
        color='neutral'
        style={{ zIndex: zIndex > 0 ? zIndex - 1 : zIndex, ...style }}
        {...rest}
        onClick={handleButtonClick}
        onMouseDown={handleButtonMouseDown}
      />
    </Menu>
  );
};
